import { Button, InputNumber, Modal, message } from 'antd';
import i18next from 'i18next';
import React, { useEffect, useState } from 'react';
import { MARMOTTE_POSES } from './Marmotte';
import { DTFItem } from './packing';
import { QualityInfo } from './quality';
import { rangeText } from './QualityModal';
import { DTFSettings } from './settings';
import { ThinFixMode } from './thinfix';
import { Palette } from './TextModal';
import { TutoLink } from './Tutos';

interface IProps {
	items: DTFItem[];
	settings: DTFSettings;
	rejected: string[];
	thin: string[];
	lowDpi: Record<string, number>;
	minDpi: number;
	quality: Record<string, QualityInfo>;
	onInspect: (id: string) => void;
	/** carte à ouvrir (logo importé, assistant fermé) — Daniel 09.10 */
	openCardId?: string | null;
	/** vectorisation gratuite (Daniel 09.10) */
	onVectorize?: (id: string) => void;
	vectorizing?: string[];
	/** rouvre l'assistant « Préparer le logo » (écrans simples) */
	onPrepare: (id: string) => void;
	upscaling: string[];
	onUpscale: (id: string) => void;
	onSmooth?: (id: string) => Promise<boolean>;
	smoothing?: string[];
	fixingThin: string[];
	onFixThin: (id: string, mode: ThinFixMode, garmentColor?: string) => void;
	onBgEdit: (id: string) => void;
	onChange: (id: string, patch: Partial<DTFItem>) => void;
	onRecolor: (id: string, color: string | null) => void;
	onRemove: (id: string) => void;
	onDuplicate: (id: string) => void;
	selectedId: string | null;
	onSelect: (id: string) => void;
	onHoverItem: (id: string | null) => void;
	onImportClick: () => void;
	onTextClick: () => void;
	onEditText: (id: string) => void;
	/** bibliothèque (15.10.2026) : connecté ? nombre de logos gardés, ouverture, « Garder » depuis la carte */
	canLibrary?: boolean;
	libraryCount?: number | null;
	onLibraryClick?: () => void;
	onSaveToLibrary?: (id: string) => void;
	savingIds?: string[];
	/** section 1 « Sur quel textile vas-tu poser ces transferts ? » (maquette v3.3, Daniel 15.10.2026) : une colle par planche */
	colle?: 'base' | 'anti' | null;
	colleFlash?: boolean;
	onChooseColle?: (g: 'base' | 'anti') => void;
}

const round1 = (v: number) => Math.round(v * 10) / 10;

/** Autorise la virgule décimale suisse/française (8,5 → 8.5) */
const commaParser = (v: string | undefined) => Number((v ?? '').replace(',', '.'));

const ItemsPanel: React.FC<IProps> = ({
	items,
	settings,
	rejected,
	thin,
	lowDpi,
	quality,
	onInspect,
	openCardId,
	onVectorize,
	vectorizing = [],
	onSmooth,
	smoothing = [],
	onPrepare,
	onBgEdit,
	onChange,
	onRecolor,
	onRemove,
	onDuplicate,
	selectedId,
	onSelect,
	onHoverItem,
	onImportClick,
	onTextClick,
	onEditText,
	canLibrary = false,
	libraryCount = null,
	onLibraryClick,
	onSaveToLibrary,
	savingIds = [],
	colle = null,
	colleFlash = false,
	onChooseColle,
}) => {
	const usableW = round1(settings.laizeCm);
	const usableH = round1(settings.maxPlancheCm);
	// carte dont la palette « Couleur unique » est ouverte (#221)
	const [colorOpenId, setColorOpenId] = useState<string | null>(null);
	// carte dont les outils secondaires (« Plus ») sont dépliés (revue UX 15.10 : 3 actions visibles, le reste derrière « Plus »)
	const [moreOpenId, setMoreOpenId] = useState<string | null>(null);
	// textile : une fois choisi, la section se replie en une ligne (Daniel 15.10) ; « Modifier » la rouvre
	const [textileOpen, setTextileOpen] = useState<boolean>(() => !colle);
	const [textileHelp, setTextileHelp] = useState(false);
	// Daniel 21.10.2026 : un « ? » dans chaque case ouvre une fenêtre avec la photo du textile et l'explication (même contenu que le site)
	const [colleInfo, setColleInfo] = useState<'base' | 'anti' | null>(null);
	const colleInfoDot = (g: 'base' | 'anti') => (
		<span role="button" tabIndex={0} className="dtf-colle-info" aria-label={i18next.t('dtf.textile-info-aria') as string} title={i18next.t('dtf.textile-info-aria') as string}
			onClick={(e) => { e.stopPropagation(); setColleInfo(g); }}
			onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setColleInfo(g); } }}>?</span>
	);
	useEffect(() => {
		if (colleFlash) setTextileOpen(true);
	}, [colleFlash]);
	// Marco 09.10.2026 : cartes repliées (image + quantité), UNE seule ouverte à la fois ; la sélection sur la planche
	// ouvre la carte. Porte de service `?cartes=toutes` (tests, pilotage Paddy) : tout reste ouvert comme avant.
	const allOpen = typeof window !== 'undefined' && /[?&]cartes=toutes/.test(window.location.search);
	const [openId, setOpenId] = useState<string | null>(null);
	useEffect(() => {
		if (selectedId) setOpenId(selectedId);
	}, [selectedId]);
	useEffect(() => {
		if (openCardId) setOpenId(openCardId);
	}, [openCardId]);
	const isOpen = (id: string) => allOpen || openId === id;
	const toggleOpen = (id: string) => {
		setOpenId(cur => (cur === id ? null : id));
		onSelect(id);
	};

	const lockToggle = (free: boolean | undefined, onToggle: (nextFree: boolean) => void) => (
		<button
			type="button"
			className={`dtf-lock-btn${free ? ' dtf-lock-free' : ''}`}
			title={i18next.t(free ? 'dtf.lock-off' : 'dtf.lock-on') as string}
			onClick={() => onToggle(!free)}
		>
			{free ? '🔓' : '🔗'}
		</button>
	);

	const setItemFree = (it: DTFItem, nextFree: boolean) => {
		if (nextFree) onChange(it.id, { free: true });
		// reverrouillage : la hauteur revient aux proportions de la police
		else onChange(it.id, { free: false, heightCm: round1(it.widthCm / it.aspect) });
	};

	const groupCard = (gid: string, g: DTFItem[]) => {
		const free = g.every(m => m.free);
		return (
			<div className="dtf-group-card" key={`g-${gid}`} data-testid="group-card">
				<div className="dtf-group-title">{i18next.t('dtf.groupe-serie', { n: g.length })}</div>
				<div className="dtf-item-fields">
					<label>
						{i18next.t('dtf.hauteur')} (cm)
						<InputNumber
							min={1}
							max={usableH}
							step={0.5}
							parser={commaParser}
							value={g[0].heightCm}
							onChange={v => {
								const raw = round1(Number(v) || 1);
								let clamped = false;
								g.forEach(m => {
									const maxH = m.free ? usableH : Math.min(usableH, round1(usableW / m.aspect));
									const h = Math.min(raw, maxH);
									if (h < raw) clamped = true;
									onChange(m.id, m.free ? { heightCm: h } : { heightCm: h, widthCm: round1(h * m.aspect) });
								});
								if (clamped)
									message.warning({ content: i18next.t('dtf.clamp-hauteur', { max: usableH }), key: 'clamp' });
							}}
						/>
					</label>
					{lockToggle(free, nextFree => g.forEach(m => setItemFree(m, nextFree)))}
					<label>
						{i18next.t('dtf.quantite')}
						<InputNumber
							min={1}
							max={500}
							step={1}
							value={g[0].qty}
							onChange={v => {
								const q = Math.max(1, Math.round(Number(v) || 1));
								g.forEach(m => onChange(m.id, { qty: q }));
							}}
						/>
					</label>
				</div>
				<div className="dtf-group-hint">{i18next.t('dtf.groupe-aide')}</div>
			</div>
		);
	};

	const cards: React.ReactNode[] = [];
	const seenGroups = new Set<string>();
	items.forEach(it => {
		if (it.groupId && !seenGroups.has(it.groupId)) {
			seenGroups.add(it.groupId);
			const g = items.filter(x => x.groupId === it.groupId);
			if (g.length > 1) cards.push(groupCard(it.groupId, g));
		}
		const q = quality[it.id] && quality[it.id].src === it.src ? quality[it.id] : undefined;
		const range = q ? rangeText(q, it.kind) : null;
		cards.push(
			<div
				key={it.id}
				data-item={it.id}
				className={`dtf-item-card${rejected.includes(it.id) ? ' dtf-item-rejected' : ''}${
					selectedId === it.id ? ' dtf-item-selected' : ''
				}${isOpen(it.id) ? ' dtf-item-open' : ' dtf-item-collapsed-card'}`}
				onMouseEnter={() => onHoverItem(it.id)}
				onMouseLeave={() => onHoverItem(null)}
			>
				<div
					className="dtf-item-head"
					title={i18next.t(isOpen(it.id) ? 'dtf.carte-replier' : 'dtf.carte-ouvrir') as string}
					onClick={() => toggleOpen(it.id)}
				>
					<img src={it.src} alt="" className="dtf-item-thumb" draggable={false} />
					{isOpen(it.id) ? (
						<div className="dtf-item-name">
							{it.name}
						</div>
					) : (
						<div className="dtf-item-collapsed">
							<label className="dtf-item-qty-mini" onClick={e => e.stopPropagation()}>
								<span>{i18next.t('dtf.quantite')}</span>
								<InputNumber
									size="small"
									min={1}
									max={500}
									step={1}
									value={it.qty}
									onChange={v => onChange(it.id, { qty: Math.max(1, Math.round(Number(v) || 1)) })}
								/>
							</label>
							<span className="dtf-item-mini-status" title={i18next.t('dtf.carte-ouvrir') as string}>
								{(() => {
									const q0 = quality[it.id] && quality[it.id].src === it.src ? quality[it.id] : undefined;
									const r0 = q0 ? rangeText(q0, it.kind) : null;
									const bad0 = thin.includes(it.id) || !!(r0 && r0.bad) || lowDpi[it.id] !== undefined;
									return !q0 ? '…' : bad0 ? '⚠️' : '✅';
								})()}
							</span>
						</div>
					)}
					{isOpen(it.id) && it.kind !== 'raster' && (
						<span className={`dtf-item-badge dtf-badge-${it.kind}`}>
							{i18next.t(it.kind === 'svg' ? 'dtf.badge-vectoriel' : 'dtf.badge-pdf')}
						</span>
					)}
					{isOpen(it.id) && it.improved && (
						<span className="dtf-item-badge dtf-badge-improved" title={i18next.t('dtf.image-amelioree') as string}>
							✨ {i18next.t('dtf.badge-amelioree')}
						</span>
					)}
					{isOpen(it.id) && (it.library || it.savedLogo) && (
						<span className="dtf-item-badge dtf-badge-library" title={i18next.t('dtf.biblio-badge-aide') as string}>
							📚 {i18next.t(it.library ? 'dtf.biblio-badge' : 'dtf.biblio-badge-garde')}
						</span>
					)}
					<span className="dtf-item-chevron" aria-hidden="true">{isOpen(it.id) ? '▾' : '▸'}</span>
				</div>
				{isOpen(it.id) && (<>
				<div className="dtf-item-fields">
					<label>
						{i18next.t('dtf.largeur')} (cm)
						<InputNumber
							min={1}
							max={usableW}
							step={0.5}
							parser={commaParser}
							value={it.widthCm}
							onChange={v => {
								let w = round1(Number(v) || 1);
								const maxW = it.free ? usableW : Math.min(usableW, round1(usableH * it.aspect));
								if (w > maxW) {
									w = maxW;
									message.warning({ content: i18next.t('dtf.clamp-largeur', { max: maxW }), key: 'clamp' });
								}
								onChange(it.id, it.free ? { widthCm: w } : { widthCm: w, heightCm: round1(w / it.aspect) });
							}}
						/>
					</label>
					{it.text && lockToggle(it.free, nextFree => setItemFree(it, nextFree))}
					<label>
						{i18next.t('dtf.hauteur')} (cm)
						<InputNumber
							min={1}
							max={usableH}
							step={0.5}
							parser={commaParser}
							value={it.heightCm}
							onChange={v => {
								let h = round1(Number(v) || 1);
								const maxH = it.free ? usableH : Math.min(usableH, round1(usableW / it.aspect));
								if (h > maxH) {
									h = maxH;
									message.warning({ content: i18next.t('dtf.clamp-hauteur', { max: maxH }), key: 'clamp' });
								}
								onChange(it.id, it.free ? { heightCm: h } : { heightCm: h, widthCm: round1(h * it.aspect) });
							}}
						/>
					</label>
					<label>
						{i18next.t('dtf.quantite')}
						<InputNumber
							min={1}
							max={500}
							step={1}
							value={it.qty}
							onChange={v => onChange(it.id, { qty: Math.max(1, Math.round(Number(v) || 1)) })}
						/>
					</label>
				</div>
				{/* 07.10.2026 : plus de fiche technique dans la carte (dpi, plage, boutons correctifs) — un statut en une
				    ligne, « Préparer » rouvre l'assistant, « Détails » garde l'accès expert (revue Neo A4) */}
				{it.library ? (
					<div className="dtf-quality-line dtf-item-status dtf-item-ok dtf-item-library">
						<span className="dtf-quality-txt">✅ {i18next.t('dtf.biblio-item-pret')}</span>
					</div>
				) : (() => {
					const bad = thin.includes(it.id) || !!(range && range.bad) || lowDpi[it.id] !== undefined;
					const analysing = !q;
					return (
						<div className={`dtf-quality-line dtf-item-status${bad ? ' dtf-quality-bad dtf-item-thin-warn' : ' dtf-item-ok'}`}>
							<span className="dtf-quality-txt" title={bad ? (i18next.t('dtf.item-a-verifier-aide') as string) : undefined}>
								{analysing ? i18next.t('dtf.qualite-analyse') : bad ? `⚠️ ${i18next.t('dtf.item-a-verifier')}` : `✅ ${i18next.t('dtf.item-pret')}`}
							</span>
							<button type="button" className="dtf-prepare-btn" title={i18next.t('dtf.prep-titre') as string} onClick={() => onPrepare(it.id)}>
								🧭 {i18next.t('dtf.item-preparer')}
							</button>
							<button type="button" className="dtf-quality-btn" title={i18next.t('dtf.qualite-titre') as string} onClick={() => onInspect(it.id)}>
								🔍 {i18next.t('dtf.item-details')}
							</button>
						</div>
					);
				})()}
				{rejected.includes(it.id) && (
					<div className="dtf-item-warn">{i18next.t('dtf.trop-grand', { w: usableW, h: usableH })}</div>
				)}
				{/* revue UX 15.10 : 3 actions visibles (Préparer, Détails, Retirer) ; le reste derrière « Plus ». `?cartes=toutes` montre tout. */}
				<div className="dtf-item-actions dtf-item-actions-main">
					{it.text && (
						<Button size="small" onClick={() => onEditText(it.id)}>
							✏️ {i18next.t('dtf.modifier')}
						</Button>
					)}
					<Button size="small" className="dtf-more-btn" aria-expanded={allOpen || moreOpenId === it.id} onClick={() => setMoreOpenId(cur => (cur === it.id ? null : it.id))}>
						{i18next.t('dtf.plus')} {allOpen || moreOpenId === it.id ? '▴' : '▾'}
					</Button>
					<Button danger size="small" className="dtf-remove-btn" onClick={() => onRemove(it.id)}>
						{i18next.t('dtf.supprimer')}
					</Button>
				</div>
				{(allOpen || moreOpenId === it.id) && (
				<div className="dtf-item-actions dtf-item-actions-more">
					{canLibrary && !it.library && !it.savedLogo && !it.text && onSaveToLibrary && (
						<Button size="small" className="dtf-lib-save-btn" data-testid="lib-save" loading={savingIds.includes(it.id)} title={i18next.t('dtf.biblio-garder-aide') as string} onClick={() => onSaveToLibrary(it.id)}>
							📚 {i18next.t('dtf.biblio-garder')}
						</Button>
					)}
					{!it.library && it.kind !== 'svg' && (
						<Button
							size="small"
							className="dtf-bg-btn"
							title={i18next.t('dtf.detourage-titre') as string}
							onClick={() => onBgEdit(it.id)}
						>
							✂️ {i18next.t('dtf.detourage')}
						</Button>
					)}
					{!it.library && it.kind !== 'svg' && !it.text && onSmooth && (
						<Button size="small" className="dtf-smooth-btn" data-testid="smooth-btn" loading={smoothing.includes(it.id)} title={i18next.t('dtf.lisser-titre') as string} onClick={() => onSmooth(it.id)}>
							🪄 {i18next.t('dtf.lisser')}
						</Button>
					)}
					{!it.library && it.kind !== 'svg' && !it.text && onVectorize && (
						<Button
							size="small"
							className="dtf-vecto-btn"
							data-testid="vecto-btn"
							loading={vectorizing.includes(it.id)}
							title={i18next.t('dtf.vecto-titre') as string}
							onClick={() => onVectorize(it.id)}
						>
							✒️ {i18next.t('dtf.vecto')}
						</Button>
					)}
					{/* 26.10 : aussi sur un logo de la bibliothèque (le serveur recolore son vrai fichier à la commande, la bibliothèque ne change pas) */}
					{!it.text && it.library?.kind !== 'texte' && (
						<Button
							size="small"
							className="dtf-recolor-btn"
							data-testid="recolor-btn"
							title={i18next.t('dtf.couleur-unique-titre') as string}
							onClick={() => setColorOpenId(colorOpenId === it.id ? null : it.id)}
						>
							🎨 {i18next.t('dtf.couleur-unique')}
							{it.monoColor && <span className="dtf-recolor-dot" style={{ background: it.monoColor }} />}
						</Button>
					)}
					<Button
						size="small"
						className="dtf-dup-btn"
						title={i18next.t('dtf.dupliquer-titre') as string}
						onClick={() => onDuplicate(it.id)}
					>
						⧉ {i18next.t('dtf.dupliquer')}
					</Button>
				</div>
				)}
				{colorOpenId === it.id && !it.text && (
					<div className="dtf-recolor-row" data-testid="recolor-row">
						<Palette value={it.monoColor || '#111111'} onChange={c => onRecolor(it.id, c)} />
						{it.monoColor && (
							<Button size="small" className="dtf-recolor-reset" onClick={() => onRecolor(it.id, null)}>
								↩ {i18next.t('dtf.couleur-origine')}
							</Button>
						)}
						<div className="dtf-recolor-hint">{i18next.t('dtf.couleur-unique-aide')}</div>
						<TutoLink n={10} />
					</div>
				)}
				</>)}
			</div>,
		);
	});

	return (
		<aside className="dtf-items-panel">
			{onChooseColle && (
				<section className={`dtf-textile${colle ? ' is-done' : ' is-todo'}${colleFlash ? ' dtf-colle-flash' : ''}${textileOpen ? ' is-open' : ' is-collapsed'}`} data-testid="colle" aria-labelledby="dtf-textile-title">
					<div className="dtf-section-head">
						<span className="dtf-section-num" aria-hidden="true">1</span>
						<h3 id="dtf-textile-title" className="dtf-section-title">{i18next.t('dtf.textile-question')}</h3>
						<span className={`dtf-section-state${colle ? ' is-done' : ' is-todo'}`}>{colle ? '✓' : `⚠ ${i18next.t('dtf.colle-a-choisir')}`}</span>
					</div>
					{textileOpen ? (
						<>
							<div className="dtf-colle-btns" role="radiogroup" aria-label={i18next.t('dtf.textile-question') as string}>
								<button type="button" role="radio" aria-checked={colle === 'base'} className={`dtf-colle-btn${colle === 'base' ? ' is-on' : ''}`} data-val="base" onClick={() => { onChooseColle('base'); setTextileOpen(false); }}>
									<strong>{i18next.t('dtf.textile-base')}</strong>
									<small>{i18next.t('dtf.textile-base-sub')}</small>
									{colleInfoDot('base')}
								</button>
								<button type="button" role="radio" aria-checked={colle === 'anti'} className={`dtf-colle-btn${colle === 'anti' ? ' is-on' : ''}`} data-val="anti" onClick={() => { onChooseColle('anti'); setTextileOpen(false); }}>
									<strong>{i18next.t('dtf.textile-anti')}</strong>
									<small>{i18next.t('dtf.textile-anti-sub')}</small>
									{colleInfoDot('anti')}
								</button>
							</div>
							<Modal open={!!colleInfo} onCancel={() => setColleInfo(null)} footer={null} width={420} centered destroyOnClose className="dtf-colle-modal"
								title={colleInfo ? (i18next.t(colleInfo === 'anti' ? 'dtf.textile-anti' : 'dtf.textile-base') as string) : ''}>
								{colleInfo && (
									<div className="dtf-colle-info-body">
										<img className="dtf-colle-info-img" src={colleInfo === 'anti' ? '/assets/colle/polyester-sublime.webp?v=20261021' : '/assets/colle/coton.webp?v=20261021'} alt="" loading="lazy" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
										<div className="dtf-colle-info-leg">{i18next.t(colleInfo === 'anti' ? 'dtf.textile-info-anti-legende' : 'dtf.textile-info-base-legende')}</div>
										{(i18next.t(colleInfo === 'anti' ? 'dtf.textile-info-anti-texte' : 'dtf.textile-info-base-texte') as string).split('\n').map((l, i) => <p key={i}>{l}</p>)}
										<Button type="primary" block onClick={() => setColleInfo(null)}>{i18next.t('dtf.textile-info-ok')}</Button>
									</div>
								)}
							</Modal>
							<button type="button" className="dtf-textile-help-btn" aria-expanded={textileHelp} onClick={() => setTextileHelp(h => !h)}>
								{i18next.t('dtf.textile-je-sais-pas')}
							</button>
							{/* Daniel 21.10.2026 : « Je ne sais pas » ouvre une fenêtre avec les deux tissus en photo et le moyen de les reconnaître ; on peut choisir depuis la fenêtre */}
							<Modal open={textileHelp} onCancel={() => setTextileHelp(false)} footer={null} width={640} centered destroyOnClose className="dtf-colle-modal dtf-textile-help-modal" title={i18next.t('dtf.textile-aide-titre') as string}>
								<p className="dtf-textile-help-intro">{i18next.t('dtf.textile-je-sais-pas-aide')}</p>
								<div className="dtf-textile-help-cols">
									{(['base', 'anti'] as const).map((g) => (
										<div key={g} className="dtf-textile-help-col">
											<img className="dtf-colle-info-img" src={g === 'anti' ? '/assets/colle/polyester-sublime.webp?v=20261021' : '/assets/colle/coton.webp?v=20261021'} alt="" loading="lazy" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
											<strong>{i18next.t(g === 'anti' ? 'dtf.textile-anti' : 'dtf.textile-base')}</strong>
											<div className="dtf-colle-info-leg">{i18next.t(g === 'anti' ? 'dtf.textile-info-anti-legende' : 'dtf.textile-info-base-legende')}</div>
											{(i18next.t(g === 'anti' ? 'dtf.textile-info-anti-texte' : 'dtf.textile-info-base-texte') as string).split('\n').map((l, i) => <p key={i}>{l}</p>)}
											<Button type={g === 'anti' ? 'default' : 'primary'} block onClick={() => { onChooseColle(g); setTextileHelp(false); setTextileOpen(false); }}>{i18next.t(g === 'anti' ? 'dtf.textile-aide-choisir-anti' : 'dtf.textile-aide-choisir-base')}</Button>
										</div>
									))}
								</div>
							</Modal>
							<div className="dtf-textile-note">{i18next.t('dtf.textile-une-colle')}</div>
						</>
					) : (
						<button type="button" className="dtf-textile-summary" onClick={() => setTextileOpen(true)} title={i18next.t('dtf.modifier') as string}>
							<span className="dtf-textile-summary-txt">
								{i18next.t(colle === 'anti' ? 'dtf.textile-anti' : 'dtf.textile-base')} · {i18next.t(colle === 'anti' ? 'dtf.textile-anti-sub' : 'dtf.textile-base-sub')}
							</span>
							<span className="dtf-textile-edit">{i18next.t('dtf.modifier')}</span>
						</button>
					)}
				</section>
			)}
			<div className="dtf-section-head dtf-logos-head">
				<span className="dtf-section-num" aria-hidden="true">2</span>
				<h3 className="dtf-section-title dtf-items-title">{i18next.t('dtf.section-logos')}</h3>
				{items.length > 0 && <span className="dtf-section-state is-done">{i18next.t('dtf.logos-n', { n: items.length })}</span>}
			</div>
			<Button type="primary" block size="large" onClick={onImportClick} className="dtf-import-btn">
				+ {i18next.t('dtf.importer')}
			</Button>
			<div className="dtf-import-hint">{i18next.t('dtf.importer-aide')}</div>
			{onLibraryClick && (
				<Button block size="large" onClick={onLibraryClick} className="dtf-lib-btn" data-testid="lib-open">
					{canLibrary ? i18next.t('dtf.biblio-bouton', { n: libraryCount === null ? '…' : libraryCount }) : i18next.t('dtf.biblio-bouton-connexion')}
				</Button>
			)}
			<Button block size="large" onClick={onTextClick} className="dtf-text-btn">
				{i18next.t('dtf.creer-numeros')}
			</Button>
			<div className="dtf-import-hint">{i18next.t('dtf.creer-numeros-aide')}</div>
			{items.length === 0 && (
				<div className="dtf-items-empty">
					<img src={MARMOTTE_POSES.point} className="dtf-mmt-mini" alt="" draggable={false} />
					{i18next.t('dtf.aucun-logo')}
				</div>
			)}
			{cards}
			{items.length > 0 && (
				<Button block size="large" onClick={onImportClick} className="dtf-import-more-btn" data-testid="import-more">
					+ {i18next.t('dtf.importer-encore')}
				</Button>
			)}
		</aside>
	);
};

export default ItemsPanel;
